import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { avatarMarkup, avatars, fallback, moods, privateAvatars } from '../../../../../brand/avatars.mjs';

// The same shapes as the mobile app and the website, from brand/avatars.mjs: the Talkos figure on a
// tile of the employee's color, in their own pose, with the thing they work with.

export type Mood = 'normal' | 'happy' | 'sleepy';

const poses = { ...avatars, ...privateAvatars };

interface Props {
  avatar: string;
  color: string;
  size?: number;
  /** The figure hops now and then. */
  animate?: boolean;
  mood?: Mood;
  /** The employee's name for screen readers; without it the avatar is decoration. */
  label?: string;
}

export function EmployeeAvatar({ avatar, color, size = 44, animate = false, mood = 'normal', label }: Props) {
  const markup = useMemo(() => {
    // A role this build has no drawing for yet (new modules arrive from the server) gets the plain figure.
    const pose = mood !== 'normal' ? moods[mood] : Object.hasOwn(poses, avatar) ? poses[avatar]! : fallback;
    // Generated from our own shapes, never from data.
    return `<rect width="100" height="100" rx="34" fill="${color}"/>${avatarMarkup(pose, color)}`;
  }, [avatar, color, mood]);
  const hop = useHop(animate);
  return (
    <svg
      className={hop ? 'avatar hop' : 'avatar'}
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}

/** Every few seconds, a short hop (none when the person asked for less motion). */
function useHop(enabled: boolean): boolean {
  const [hop, setHop] = useState(false);
  useEffect(() => {
    const lessMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!enabled || lessMotion) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(
        () => {
          setHop(true);
          timer = setTimeout(() => {
            setHop(false);
            schedule();
          }, 420);
        },
        2600 + Math.random() * 4800,
      );
    };
    schedule();
    return () => clearTimeout(timer);
  }, [enabled]);
  return hop;
}

/** A few employees' faces, overlapping: who would take the work. */
export function AvatarStack({ people, size = 32 }: { people: { avatar: string; color: string; name: string }[]; size?: number }) {
  return (
    <span className="avatar-stack" style={{ '--size': `${size}px` } as CSSProperties}>
      {people.map((p) => (
        <EmployeeAvatar key={p.name} avatar={p.avatar} color={p.color} size={size} label={p.name} />
      ))}
    </span>
  );
}
